function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { emailValidation, nameValidation, requiredValidation } from 'validators';
import { TextField, PasswordField } from 'seagull';
import GoogleButton from "../../../components/GoogleButton/GoogleButton";
import IntuitButton from "../../../components/IntuitButton/IntuitButton";
import { routes, UtmParams } from "../../../constants";
import { useStoreActions } from "../../../contexts";
import { useEventReporter } from "../../../event-reporter";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { useOnMount } from "../../../hooks/useOnMount";
import { useSetDocumentTitle } from "../../../hooks/useSetDocumentTitle";
import { useUtmsParams } from "../../../hooks/useUtmsParams";
import { ErrorResponse, ServerError } from "../../../infra/ErrorResponse";
import { handlePromise } from "../../../infra/utils";
import { FormSeparatorVertical } from "../components/FormSeparator";
import { useLocationCommonActions } from "../../../providers/LocationProvider";
import { FormContainer, FormTitle, FormFields, VerticalFormFields, HorizontalFormFields, SubmitButtonField, ErrorMessage, Footer, FooterText, FooterLink, FormSubTitle } from "../components/FormComponents";
import TermsAndPrivacyMessage from "../components/TermsAndPrivacyMessage";
import UnauthorizedPageContainer from "../components/UnauthorizedPageContainer";
import XeroSignInButton from "../components/XeroSignInButton/XeroSignInButton";
import { errorMessages } from "../constants";
import { LoginFlow } from "../LoginScreen/LoginScreen.types";
import OTPForm from "../LoginScreen/Steps/OTPForm";
import styles from "./SignupScreen.module.scss";

//Make sure to update the hubspot schema when adding/removing fields
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
export function SignupForm(props) {
  const {
    onSubmitted,
    visitorSyncFrame,
    visitorSyncOrigin
  } = props;
  const {
    logBI,
    api
  } = useStoreActions();
  const history = useHistory();
  const {
    reportFormSubmission,
    reportToHubspot,
    prepareFormObject
  } = useEventReporter();
  const [signupInProgress, setSignupInProgress] = useState(false);
  const [signupError, setSignupError] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const getUtms = useUtmsParams();
  const {
    getPageUrl
  } = useLocationCommonActions();
  const {
    LOGIN_WITH_XERO
  } = useFeatureFlags();
  const {
    handleSubmit,
    control,
    formState
  } = useForm({
    mode: 'onChange'
  });
  useOnMount(() => {
    const utms = getUtms();
    if (UtmParams.Referral in utms) {
      logBI(18151, {
        referrer: utms[UtmParams.Referral]
      });
    }
  });
  const onSubmit = async data => {
    const {
      fullName,
      email,
      password
    } = data;
    logBI(10206, {});
    setSignupInProgress(true);
    const signup = await handlePromise(api.auth.signup({
      fullName,
      email,
      password,
      utms: getUtms(),
      pageUrl: getPageUrl()
    }));
    if (!signup.success) {
      setSignupError(true);
      setSignupInProgress(false);
      const error = new ErrorResponse(signup.error);
      if (error.hasServerError(ServerError.EmailIsInvalid)) {
        setErrorMessage(errorMessages.noBusinessEmailError);
      } else if (error.hasServerError(ServerError.AccountAlreadyExists)) {
        setErrorMessage( /*#__PURE__*/_jsxs(_Fragment, {
          children: ["There is already a user with this email address.", ' ', /*#__PURE__*/_jsx(FooterLink, {
            toInternalLink: routes.login,
            underline: "always",
            "data-testid": "try-logging-in-link",
            children: "Try logging in instead"
          })]
        }));
      } else {
        setErrorMessage(errorMessages.generalSignupError);
      }
    } else {
      reportFormSubmission('app-sign-up', [prepareFormObject('email', email)]); //TODO - replace this with specific events
      reportToHubspot('app_sign_up_', {
        email,
        firstname: fullName.first,
        lastname: fullName.last
      });
      setSignupInProgress(false);
      onSubmitted(email);
      visitorSyncOrigin !== '' && (visitorSyncFrame === null || visitorSyncFrame === void 0 ? void 0 : visitorSyncFrame.postMessage({
        type: 'anc_tracker_request',
        action: 'sync_user',
        data: {
          userId: signup.data.id,
          properties: {},
          origin: window.location.origin,
          path: window.location.pathname
        }
      }, "https://".concat(visitorSyncOrigin)));
    }
  };
  return /*#__PURE__*/_jsxs(FormContainer, {
    id: "signup-form",
    onSubmit: handleSubmit(onSubmit),
    "data-testid": "signup-form",
    onChange: () => setSignupError(false),
    children: [/*#__PURE__*/_jsx(FormTitle, {
      children: "Sign up"
    }), /*#__PURE__*/_jsxs(FormSubTitle, {
      children: [/*#__PURE__*/_jsx(FooterText, {
        children: " Already have an account? "
      }), /*#__PURE__*/_jsx(FooterLink, {
        toInternalLink: routes.login,
        "data-testid": "sign-in-link",
        children: "Log in"
      })]
    }), /*#__PURE__*/_jsx(FormFields, {
      children: /*#__PURE__*/_jsxs(HorizontalFormFields, {
        className: styles.formWrapper,
        children: [/*#__PURE__*/_jsxs(VerticalFormFields, {
          children: [/*#__PURE__*/_jsxs(HorizontalFormFields, {
            children: [/*#__PURE__*/_jsx(Controller, {
              control: control,
              name: "fullName.first",
              defaultValue: "",
              rules: nameValidation('First name', {
                required: 'Please enter a first name'
              }),
              render: _ref => {
                var _fieldState$error;
                let {
                  field,
                  fieldState
                } = _ref;
                return /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
                  collapseHelperText: false
                }, field), {}, {
                  label: "First name",
                  error: fieldState.isTouched && !!fieldState.error,
                  helperText: fieldState.isTouched && ((_fieldState$error = fieldState.error) === null || _fieldState$error === void 0 ? void 0 : _fieldState$error.message),
                  testid: "first-name-input"
                }));
              }
            }), /*#__PURE__*/_jsx(Controller, {
              control: control,
              name: "fullName.last",
              defaultValue: "",
              rules: nameValidation('Last name', {
                required: 'Please enter a last name'
              }),
              render: _ref2 => {
                var _fieldState$error2;
                let {
                  field,
                  fieldState
                } = _ref2;
                return /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
                  collapseHelperText: false
                }, field), {}, {
                  label: "Last name",
                  error: fieldState.isTouched && !!fieldState.error,
                  helperText: fieldState.isTouched && ((_fieldState$error2 = fieldState.error) === null || _fieldState$error2 === void 0 ? void 0 : _fieldState$error2.message),
                  testid: "last-name-input"
                }));
              }
            })]
          }), /*#__PURE__*/_jsx(Controller, {
            control: control,
            name: "email",
            defaultValue: "",
            rules: emailValidation(undefined, {
              required: 'Please enter an email'
            }),
            render: _ref3 => {
              var _fieldState$error3;
              let {
                field,
                fieldState
              } = _ref3;
              return /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
                collapseHelperText: false
              }, field), {}, {
                label: "Work email",
                type: "email",
                error: fieldState.isTouched && !!fieldState.error,
                helperText: fieldState.isTouched && ((_fieldState$error3 = fieldState.error) === null || _fieldState$error3 === void 0 ? void 0 : _fieldState$error3.message),
                testid: "email-input"
              }));
            }
          }), /*#__PURE__*/_jsx(Controller, {
            control: control,
            name: "password",
            defaultValue: "",
            rules: _objectSpread(_objectSpread({}, requiredValidation('Please enter a password')), {}, {
              minLength: {
                value: 10,
                message: 'Use 10 or more characters'
              }
            }),
            render: _ref4 => {
              var _fieldState$error4;
              let {
                field,
                fieldState
              } = _ref4;
              return /*#__PURE__*/_jsx(PasswordField, _objectSpread(_objectSpread({}, field), {}, {
                onBlur: () => {
                  logBI(10203, {
                    isPasswordValid: !fieldState.error
                  });
                  field.onBlur();
                },
                error: fieldState.isTouched && !!fieldState.error,
                helperText: fieldState.isTouched && ((_fieldState$error4 = fieldState.error) === null || _fieldState$error4 === void 0 ? void 0 : _fieldState$error4.message),
                testid: "password-input"
              }));
            }
          }), /*#__PURE__*/_jsx(SubmitButtonField, {
            disabled: !formState.isValid,
            isLoading: signupInProgress,
            "data-testid": "signup-button",
            children: "Continue"
          })]
        }), /*#__PURE__*/_jsx(FormSeparatorVertical, {
          children: "Or"
        }), /*#__PURE__*/_jsxs("div", {
          className: styles.oauthButtonsContainer,
          children: [/*#__PURE__*/_jsx(GoogleButton, {
            testid: "google-btn-sign-up",
            onAuthError: msg => {
              setErrorMessage(msg || errorMessages.generalSignupError);
              setSignupError(true);
            },
            onAuthSuccess: () => {
              history.push(routes.home);
            }
          }), LOGIN_WITH_XERO && /*#__PURE__*/_jsx(XeroSignInButton, {
            location: window.location,
            mode: "signUp"
          }), /*#__PURE__*/_jsx(IntuitButton, {
            mode: "signUp"
          })]
        })]
      })
    }), signupError && /*#__PURE__*/_jsx(ErrorMessage, {
      "data-testid": "error-msg",
      children: errorMessage
    }), /*#__PURE__*/_jsx(Footer, {
      children: /*#__PURE__*/_jsx(TermsAndPrivacyMessage, {
        type: "signup"
      })
    })]
  });
}
export function SignupScreen() {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState('signup');
  const visitorSyncFrame = useMemo(() => {
    var _document$getElementB;
    return (_document$getElementB = document.getElementById('visitor-sync-frame')) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.contentWindow;
  }, []);
  useSetDocumentTitle('Anchor Signup');
  return /*#__PURE__*/_jsxs(UnauthorizedPageContainer, {
    testId: step,
    wideLayout: step === 'signup',
    otpLayout: step === 'waiting-verification',
    children: [step === 'signup' && /*#__PURE__*/_jsx(SignupForm, {
      onSubmitted: email => {
        if (email) {
          setEmail(email);
          setStep('waiting-verification');
        }
      },
      visitorSyncFrame: visitorSyncFrame,
      visitorSyncOrigin: window._visitorSyncHostname || ''
    }), step === 'waiting-verification' && /*#__PURE__*/_jsx(OTPForm, {
      email: email,
      flow: LoginFlow.EmailVerification,
      onBack: () => setStep('signup'),
      shouldRequestOTP: false
    })]
  });
}